import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DataDemoBadge, CompletenessBar } from "@/components/DataBadge";
import { formatRupiah, orDash, CATEGORY_LABEL } from "@/lib/format";
import { Calculator, Mountain, GitCompare, Bookmark, BookmarkCheck, Info, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const SPEC_GROUPS = {
  motor_listrik: [
    ["Daya nominal", "motor_nominal_w", " W"], ["Daya puncak", "motor_peak_w", " W"], ["Torsi motor", "motor_torque_nm", " Nm"],
    ["Kapasitas baterai", "battery_capacity_kwh", " kWh"], ["Tegangan baterai", "battery_voltage_v", " V"], ["Kimia baterai", "battery_chemistry", ""],
    ["Skema baterai", "battery_scheme", ""], ["Sewa baterai/bulan", "monthly_battery_rental", ""], ["Jarak klaim", "claimed_range_km", " km"],
    ["Jarak uji (maks)", "tested_range_max_km", " km"], ["Konsumsi", "efficiency_wh_per_km", " Wh/km"], ["Kecepatan maks", "top_speed_kmh", " km/jam"],
    ["Waktu pengisian", "charge_time_hours", " jam"], ["Baterai dapat dilepas", "removable_battery", ""], ["Berat", "curb_weight_kg", " kg"],
    ["Beban maks", "maximum_payload_kg", " kg"], ["Garansi kendaraan", "vehicle_warranty", ""], ["Garansi baterai", "battery_warranty", ""], ["Service center", "service_coverage", ""],
  ],
  motor_bensin: [
    ["Kapasitas mesin", "engine_cc", " cc"], ["Jenis BBM", "fuel_type", ""], ["Konsumsi BBM", "efficiency_km_per_liter", " km/l"],
    ["Kecepatan maks", "top_speed_kmh", " km/jam"], ["Berat", "curb_weight_kg", " kg"], ["Beban maks", "maximum_payload_kg", " kg"],
    ["Biaya servis/tahun", "annual_service_cost", ""], ["Garansi", "vehicle_warranty", ""], ["Service center", "service_coverage", ""],
  ],
};

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.post("/meta/track", { event: "vehicle_detail_view", meta: { vehicle_id: id } }).catch(() => {});
    api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => toast.error("Kendaraan tidak ditemukan."));
    if (user) api.get("/saved-vehicles").then((r) => setSaved(r.data.items.some((x) => x.vehicle_id === id))).catch(() => {});
  }, [id, user]);

  const toggleSave = async () => {
    if (!user) { navigate("/login"); return; }
    const { data } = await api.post(`/vehicles/${id}/save`);
    setSaved(data.saved);
    toast.success(data.saved ? "Disimpan ke favorit" : "Dihapus dari favorit");
  };

  if (!v) return <div className="max-w-6xl mx-auto px-4 py-20 text-center text-muted-foreground">Memuat…</div>;

  const specs = v.category === "motor_bensin" ? SPEC_GROUPS.motor_bensin : SPEC_GROUPS.motor_listrik;
  const fmtSpec = (key, suffix) => {
    let val = v[key];
    if (val === null || val === undefined || val === "") return "Data belum tersedia";
    if (key === "removable_battery") return val ? "Ya" : "Tidak";
    if (key === "monthly_battery_rental") return val > 0 ? formatRupiah(val) : "Tidak ada (beli putus)";
    if (key === "battery_scheme") return { beli: "Beli", sewa: "Sewa", swap: "Swap" }[val] || val;
    return `${val}${suffix}`;
  };
  const isEV = v.category !== "motor_bensin";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="vehicle-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-navy mb-4 text-sm" data-testid="detail-back"><ArrowLeft className="w-4 h-4" /> Kembali</button>

      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="rounded-3xl overflow-hidden bg-secondary aspect-[4/3] flex items-center justify-center relative">
            {v.image ? <img src={v.image} alt={v.model} className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; const fb = e.currentTarget.parentNode.querySelector("[data-fallback]"); if (fb) fb.style.display = "flex"; }} /> : null}
            <div data-fallback className="absolute inset-0 items-center justify-center text-muted-foreground" style={{ display: v.image ? "none" : "flex" }}>Tanpa foto</div>
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-navy text-white hover:bg-navy border-0">{CATEGORY_LABEL[v.category]}</Badge>
            <DataDemoBadge status={v.data_status} />
          </div>
          <p className="text-muted-foreground">{v.brand}</p>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-navy">{v.model}</h1>
          <p className="text-teal font-heading font-extrabold text-3xl mt-3">{formatRupiah(v.official_price)}</p>
          <p className="text-xs text-muted-foreground">Harga resmi (indikatif)</p>

          {/* Marketplace price */}
          <div className="mt-4 bg-white border border-border rounded-2xl p-4">
            <p className="font-semibold text-navy text-sm mb-2">Rentang Harga Marketplace</p>
            {v.marketplace_price_range ? (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><p className="text-xs text-muted-foreground">Min</p><p className="font-bold text-navy">{formatRupiah(v.marketplace_price_range.min)}</p></div>
                <div><p className="text-xs text-muted-foreground">Median</p><p className="font-bold text-navy">{formatRupiah(v.marketplace_price_range.median)}</p></div>
                <div><p className="text-xs text-muted-foreground">Maks</p><p className="font-bold text-navy">{formatRupiah(v.marketplace_price_range.max)}</p></div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground" data-testid="no-marketplace">Belum ada harga marketplace terverifikasi.</p>
            )}
            <p className="text-[11px] text-muted-foreground mt-2">Harga indikatif. Harga aktual dapat berubah. Periksa kembali pada halaman penjual.</p>
          </div>

          <div className="mt-4"><CompletenessBar value={v.specification_completeness} /></div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <Button data-testid="detail-calc-btn" onClick={() => navigate(`/hitung?ev=${v.vehicle_id}`)} className="rounded-full bg-teal hover:bg-teal-hover"><Calculator className="w-4 h-4 mr-2" />Hitung Hemat</Button>
            {isEV && <Button data-testid="detail-hill-btn" onClick={() => navigate(`/tanjakan?vehicle=${v.vehicle_id}`)} variant="outline" className="rounded-full"><Mountain className="w-4 h-4 mr-2" />Cek Tanjakan</Button>}
            <Button data-testid="detail-compare-btn" onClick={() => navigate(`/bandingkan?add=${v.vehicle_id}`)} variant="outline" className="rounded-full"><GitCompare className="w-4 h-4 mr-2" />Bandingkan</Button>
            <Button data-testid="detail-save-btn" onClick={toggleSave} variant="outline" className="rounded-full">{saved ? <><BookmarkCheck className="w-4 h-4 mr-2 text-teal" />Tersimpan</> : <><Bookmark className="w-4 h-4 mr-2" />Simpan</>}</Button>
          </div>
        </div>
      </div>

      {/* Pros/cons */}
      {(v.pros?.length || v.cons?.length) ? (
        <div className="grid md:grid-cols-2 gap-4 mt-10">
          <div className="bg-green-50 rounded-2xl p-5"><p className="font-semibold text-green-800 mb-2">Kelebihan</p><ul className="space-y-1 text-sm text-green-900">{v.pros?.map((p, i) => <li key={i}>• {p}</li>)}</ul></div>
          <div className="bg-amber-50 rounded-2xl p-5"><p className="font-semibold text-amber-800 mb-2">Keterbatasan</p><ul className="space-y-1 text-sm text-amber-900">{v.cons?.map((c, i) => <li key={i}>• {c}</li>)}</ul></div>
        </div>
      ) : null}

      {/* Specs */}
      <div className="mt-10">
        <h2 className="font-heading font-bold text-2xl text-navy mb-4">Spesifikasi</h2>
        <div className="bg-white rounded-2xl border border-border divide-y" data-testid="spec-list">
          {specs.map(([label, key, suffix]) => (
            <div key={key} className="flex items-center justify-between px-5 py-3">
              <span className="text-muted-foreground text-sm">{label}</span>
              <span className={`font-semibold text-sm ${fmtSpec(key, suffix) === "Data belum tersedia" ? "text-status-grey" : "text-navy"}`}>{fmtSpec(key, suffix)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Source */}
      <div className="mt-6 bg-slate-50 border border-border rounded-2xl p-5 flex gap-3">
        <ShieldCheck className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          <p><strong className="text-navy">Sumber data:</strong> {v.sources?.source_name || "-"} ({v.sources?.source_type || "-"})</p>
          <p><strong className="text-navy">Tanggal pembaruan:</strong> {v.updated_at?.slice(0, 10)} · Tingkat keyakinan: {v.sources?.confidence_level || "-"}</p>
          <p className="mt-2 text-xs">{v.notes}</p>
        </div>
      </div>
    </div>
  );
}
